import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, money, fileUrl } from "@/lib/api";
import { Loader, StatusBadge, Money } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Truck, FileDown, RotateCcw, Undo2, CheckCircle2 } from "lucide-react";

const STEPS = ["CONFIRMED", "PROCESSING", "DISPATCHED", "DELIVERED"];
const REASONS = ["Damaged", "Wrong Product", "Missing Quantity", "Expired/Quality Issue", "Other"];

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [o, setO] = useState(null);
  const [retOpen, setRetOpen] = useState(false);
  const [ret, setRet] = useState({ reason: "", description: "", quantity: 1 });

  const load = () => api.get(`/retailer/orders/${id}`).then((r) => setO(r.data)).catch(() => setO(false));
  useEffect(() => { load(); }, [id]);
  if (o === null) return <Loader />;
  if (!o) return <div className="py-10 text-center">Order not found.</div>;

  const stepIdx = STEPS.indexOf(o.status);

  async function reorder() {
    try { const { data } = await api.post(`/retailer/orders/${id}/reorder`); toast.success(`${data.added} item(s) added to cart`); nav("/retailer/cart"); }
    catch (e) { toast.error(apiError(e)); }
  }
  async function submitReturn() {
    if (!ret.reason) { toast.error("Select a reason"); return; }
    try { await api.post("/retailer/returns", { order_id: id, ...ret }); toast.success("Return requested"); setRetOpen(false); }
    catch (e) { toast.error(apiError(e)); }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <button onClick={() => nav("/retailer/orders")} className="flex items-center gap-1 text-sm text-ink/50 hover:text-forest"><ArrowLeft className="h-4 w-4" /> Orders</button>

      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="num font-heading text-xl font-semibold text-forest">{o.order_number}</h1>
            <p className="text-xs text-ink/50">{o.manufacturer_name} · {o.created_at.slice(0, 10)}</p>
          </div>
          <StatusBadge status={o.status} />
        </div>

        {o.payment_status === "paid" && (
          <div className="mt-5 flex items-center justify-between">
            {STEPS.map((s, i) => (
              <div key={s} className="flex flex-1 items-center">
                <div className="flex flex-col items-center">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs ${i <= stepIdx ? "bg-forest text-white" : "bg-psage text-msage"}`}>
                    {i <= stepIdx ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                  </div>
                  <span className="mt-1 text-[10px] text-ink/60">{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`mx-1 h-0.5 flex-1 ${i < stepIdx ? "bg-forest" : "bg-psage"}`} />}
              </div>
            ))}
          </div>
        )}
      </div>

      {o.tracking_id && (
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-sm font-medium text-forest"><Truck className="h-4 w-4" /> Tracking</div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
            <Info label="Courier" value={o.courier} />
            <Info label="Tracking ID" value={o.tracking_id} mono />
            {o.dispatch_date && <Info label="Dispatched" value={o.dispatch_date.slice(0, 10)} />}
            {o.expected_delivery && <Info label="Expected" value={o.expected_delivery.slice(0, 10)} />}
          </div>
          {o.tracking_url && <a href={o.tracking_url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-sm text-olive hover:underline">Track on courier site →</a>}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-5">
        <p className="mb-3 text-sm font-medium text-forest">Items</p>
        {o.items.map((i) => (
          <div key={i.product_id} className="flex items-center justify-between border-b border-border/60 py-2 text-sm last:border-0">
            <div><p className="text-ink">{i.name}</p><p className="num text-xs text-ink/50">{i.quantity} × {money(i.retailer_price)}</p></div>
            <Money value={i.line_value} className="text-forest" />
          </div>
        ))}
        <div className="mt-3 space-y-1 border-t border-border pt-3 text-sm">
          <Line label="Subtotal" value={o.subtotal} /><Line label="GST" value={o.gst_amount} />
          <Line label="Shipping" value={o.shipping} /><Line label="Total" value={o.total} bold />
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {o.invoice_id && (
          <Button data-testid="download-invoice" variant="outline" className="border-forest text-forest hover:bg-psage"
            onClick={() => window.open(fileUrl(`/api/invoices/${o.invoice_id}/pdf`) + `?t=${localStorage.getItem("script_token")}`, "_blank")}>
            <FileDown className="mr-2 h-4 w-4" /> Download Invoice
          </Button>
        )}
        <Button data-testid="reorder-btn" variant="outline" className="border-olive text-olive hover:bg-psage" onClick={reorder}><RotateCcw className="mr-2 h-4 w-4" /> Reorder</Button>
        {o.status === "DELIVERED" && (
          <Button data-testid="return-btn" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => setRetOpen(true)}><Undo2 className="mr-2 h-4 w-4" /> Request Return</Button>
        )}
      </div>

      <Dialog open={retOpen} onOpenChange={setRetOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request a return</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={ret.reason} onValueChange={(v) => setRet({ ...ret, reason: v })}>
              <SelectTrigger data-testid="return-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
              <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="return-qty" type="number" min={1} placeholder="Quantity" value={ret.quantity} onChange={(e) => setRet({ ...ret, quantity: parseInt(e.target.value) || 1 })} />
            <Textarea data-testid="return-desc" placeholder="Describe the issue" value={ret.description} onChange={(e) => setRet({ ...ret, description: e.target.value })} />
          </div>
          <DialogFooter><Button data-testid="return-submit" className="bg-forest text-white hover:bg-forest/90" onClick={submitReturn}>Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, value, mono }) => (<div><div className="overline text-olive">{label}</div><div className={mono ? "num text-ink" : "text-ink"}>{value}</div></div>);
const Line = ({ label, value, bold }) => (<div className={`flex justify-between ${bold ? "font-semibold text-forest" : "text-ink/70"}`}><span>{label}</span><Money value={value} className={bold ? "text-forest" : "text-ink"} /></div>);
